import React, { useState, useEffect, useRef } from "react";
import { playCloseSound } from "../../Sounds";

function rarityHex(r) {
  return { Common:"#9e9e9e", Uncommon:"#4caf50", Rare:"#2196f3", Epic:"#9c27b0", Legendary:"#ff9800", Mythic:"#ffeb3b", Ultra:"#ff2d78" }[r] || "#fff";
}

const RARITY_ORDER = ["Common","Uncommon","Rare","Epic","Legendary","Mythic","Ultra"];

export default function BulkRoll({ results, onDone }) {
  const [revealedCount, setRevealedCount] = useState(0);
  const [finished, setFinished]           = useState(false);
  const timerRef                          = useRef(null);

  useEffect(() => {
    if (revealedCount >= results.length) { setFinished(true); return; }
    // start slow, speed up as cards reveal
    const delay = Math.max(130, 480 - revealedCount * 32);
    timerRef.current = setTimeout(() => setRevealedCount(c => c + 1), delay);
    return () => clearTimeout(timerRef.current);
  }, [revealedCount, results.length]);

  // summary stats
  const rarityCounts = results.reduce((acc, r) => {
    acc[r.rarity] = (acc[r.rarity] || 0) + 1;
    return acc;
  }, {});
  const sortedRarities = Object.entries(rarityCounts).sort(
    ([a],[b]) => RARITY_ORDER.indexOf(b) - RARITY_ORDER.indexOf(a)
  );
  const totalCoins = results.reduce((s, r) => s + (r.earned || 0), 0);
  const best = results.reduce((b, r) =>
    RARITY_ORDER.indexOf(r.rarity) > RARITY_ORDER.indexOf(b.rarity) ? r : b
  , results[0]);
  const newCount = results.filter(r => !r.isDupe).length;

  return (
    <div className="modal-overlay">
      <div className="modal-box bulk-box" onClick={e => e.stopPropagation()}>
        <h2>🎲 Bulk Roll</h2>
        <p className="bulk-subtitle">
          {finished ? `${newCount} new • ${results.length - newCount} dupes` : `Revealing ${revealedCount} / ${results.length}…`}
        </p>

        {/* Progress bar */}
        <div className="bulk-progress">
          <div className="bulk-progress-fill" style={{ width:`${(revealedCount/results.length)*100}%` }}/>
        </div>

        {/* Card grid */}
        <div className="bulk-grid">
          {results.map((r, i) => {
            const isRevealed = i < revealedCount;
            const colour     = rarityHex(r.rarity);
            return (
              <div
                key={i}
                className={`bulk-card${isRevealed ? " revealed" : ""}`}
                style={isRevealed ? { borderColor:colour, boxShadow:`0 0 10px ${colour}44` } : {}}
              >
                {isRevealed ? (
                  <>
                    <img src={r.image} alt={r.name} className="bulk-img" />
                    <div className="bulk-rarity" style={{ color:colour }}>{r.rarity}</div>
                    {r.isDupe && <div className="bulk-dupe">♻️</div>}
                  </>
                ) : (
                  <div className="bulk-unknown">?</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Summary */}
        {finished && (
          <div className="bulk-summary">
            <div className="bulk-summary-title">Results</div>
            <div className="bulk-summary-row">
              {sortedRarities.map(([rarity, count]) => (
                <div key={rarity} className="bulk-summary-chip" style={{ color:rarityHex(rarity) }}>
                  {count}× {rarity}
                </div>
              ))}
            </div>
            <div className="bulk-summary-coins">+🪙 {totalCoins} coins earned</div>
            {best && (
              <div className="bulk-summary-best">
                ⭐ Best pull: <strong style={{ color:rarityHex(best.rarity) }}>{best.name}</strong>
              </div>
            )}
            <button onClick={() => { playCloseSound(); onDone(); }} style={{ marginTop:16 }}>Collect All</button>
          </div>
        )}
      </div>
    </div>
  );
}